import React, {
  useContext,
  useCallback,
  useRef,
} from 'react';
import { Resizable } from 'react-resizable';

import { ChatChannelListContext } from '../../../../chat';
import LayoutContext, {
  MIN_RIGHT_PANEL_WIDTH,
  MAX_RIGHT_PANEL_WIDTH,
  clampPanelWidth,
} from '../../context/LayoutContext';
import ChatSideBar from '../../../pages/Chat/components/ChatSideBar';

import { ToggleButton } from '../ToggleButton';
import {
  PanelContainer,
  CollapsedBar,
  ExpandedPanel,
  ResizeHandle,
  PanelHeader,
  StyledUnreadCount,
  PanelContent,
  StyledChevronRightIcon,
  StyledChevronLeftIcon,
} from './styles';

const RightPanel = () => {
  const {
    isChatPanelOpen,
    setIsChatPanelOpen,
    rightPanelWidth,
    setRightPanelWidth,
    rightPanelContent,
  } = useContext(LayoutContext);

  const { totalUnreadCount = 0 } = useContext(ChatChannelListContext) || {};

  const rafIdRef = useRef(null);
  const pendingWidthRef = useRef(rightPanelWidth);

  const handleToggle = useCallback(() => {
    setIsChatPanelOpen((prev) => !prev);
  }, [
    setIsChatPanelOpen,
  ]);

  const scheduleWidthUpdate = useCallback((nextWidth) => {
    pendingWidthRef.current = nextWidth;
    if (rafIdRef.current !== null) return;
    rafIdRef.current = window.requestAnimationFrame(() => {
      rafIdRef.current = null;
      setRightPanelWidth(pendingWidthRef.current, { persist: false });
    });
  }, [setRightPanelWidth]);

  const handleResize = useCallback((_e, { size }) => {
    scheduleWidthUpdate(clampPanelWidth(size.width));
  }, [scheduleWidthUpdate]);

  const handleResizeStop = useCallback((_e, { size }) => {
    if (rafIdRef.current !== null) {
      window.cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    setRightPanelWidth(clampPanelWidth(size.width), { persist: true });
  }, [setRightPanelWidth]);

  // Show collapsed bar when the panel is closed.
  if (!isChatPanelOpen) {
    return (
      <CollapsedBar onClick={handleToggle} aria-label="Expand panel">
        <StyledChevronLeftIcon />
        {totalUnreadCount > 0 && (
          <StyledUnreadCount label={totalUnreadCount} size="small" />
        )}
      </CollapsedBar>
    );
  }

  // Determine content to show
  const content = rightPanelContent || <ChatSideBar />;

  return (
    <Resizable
      width={rightPanelWidth}
      height={0}
      axis="x"
      resizeHandles={['w']}
      handle={(handleAxis, ref) => <ResizeHandle ref={ref} data-axis={handleAxis} />}
      onResize={handleResize}
      onResizeStop={handleResizeStop}
      minConstraints={[MIN_RIGHT_PANEL_WIDTH, 0]}
      maxConstraints={[MAX_RIGHT_PANEL_WIDTH, 0]}
    >
      <PanelContainer $width={rightPanelWidth}>
        <ExpandedPanel>
          <PanelHeader>
            <ToggleButton onClick={handleToggle} title="Collapse panel">
              <StyledChevronRightIcon />
            </ToggleButton>
            <StyledUnreadCount label={`${totalUnreadCount} unread`} />
          </PanelHeader>
          <PanelContent>
            {content}
          </PanelContent>
        </ExpandedPanel>
      </PanelContainer>
    </Resizable>
  );
};

export default RightPanel;
